import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { db } from '@/lib/database';

interface ConnectivityContextValue {
  isOnline: boolean;
  queueCount: number;
  refreshQueueCount: () => Promise<void>;
}

const ConnectivityContext = createContext<ConnectivityContextValue | null>(null);

export function ConnectivityProvider({ children }: { children: ReactNode }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queueCount, setQueueCount] = useState(0);

  const refreshQueueCount = useCallback(async () => setQueueCount(await db.queue.count()), []);

  useEffect(() => {
    // Connectivity only updates status. Queued orders are never synced
    // automatically; the user syncs each one from the Order Queue screen.
    void refreshQueueCount();

    const handleOnline = () => {
      setIsOnline(true);
      void refreshQueueCount();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [refreshQueueCount]);

  const value = useMemo(() => ({ isOnline, queueCount, refreshQueueCount }), [isOnline, queueCount, refreshQueueCount]);
  return <ConnectivityContext.Provider value={value}>{children}</ConnectivityContext.Provider>;
}

export function useConnectivity(): ConnectivityContextValue {
  const value = useContext(ConnectivityContext);
  if (!value) throw new Error('useConnectivity must be used inside ConnectivityProvider.');
  return value;
}
